import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { api, useAgentApi } from '@sib/api';
import { getTokens, msalInstance } from '@sib/auth';
import { env } from '@sib/config';

export type ExternalTokenRequestDto = {
  orgId: string;
};

export type ExternalTokenResponseDto = {
  access_token?: string;
  token_type?: string;
};

/**
 * How the Bearer token sent to the agent's `/auth/external-token` is obtained.
 * - `franz`: franz OBO token (`/token/franz/{orgId}`) — spendbrain (default).
 * - `entra-agent`: a delegated Entra access token whose `aud` matches the Agent
 *   middleware API (required by the internal-portal bill-pay agent).
 */
export type ExternalTokenAuthMode = 'franz' | 'entra-agent';

export interface ExternalTokenAuthConfig {
  authMode?: ExternalTokenAuthMode;
}

const acquireAgentAccessToken = async (): Promise<string> => {
  if (!env.agentApiScope) throw new Error('Agent API scope not configured');

  const tokens = await getTokens({
    instance: msalInstance,
    scopes: [env.agentApiScope],
  });

  if (!tokens?.accessToken)
    throw new Error('Failed to acquire agent access token');

  return tokens.accessToken;
};

export const useExternalTokenAuth = (
  organizationId: string,
  aiPort?: string | null,
  options?: UseMutationOptions<
    ExternalTokenResponseDto,
    Error,
    ExternalTokenRequestDto
  >,
  config?: ExternalTokenAuthConfig
) => {
  const agentApi = useAgentApi(aiPort);
  const authMode = config?.authMode ?? 'franz';

  return useMutation({
    ...options,
    mutationFn: async (data: ExternalTokenRequestDto) => {
      const bearerToken =
        authMode === 'entra-agent'
          ? await acquireAgentAccessToken()
          : await api.token.tokenControllerGetFranzOboToken(organizationId);

      const response = await agentApi.instance.get<ExternalTokenResponseDto>(
        '/auth/external-token',
        {
          params: {
            org_id: data.orgId,
            // Cache-bust: request Cache-Control is unreliable for XHR GETs; a
            // stale cached response would skip Set-Cookie and leave the iframe
            // session expired.
            _: Date.now(),
          },
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
          withCredentials: true,
        }
      );

      return response.data;
    },
  });
};
